import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"
import { requireInventoryUserId } from "@/inventory/lib/auth.server"
import {
  buildReportBreakdownCsv,
  buildReportSeriesCsv,
} from "../lib/report-export-csv"
import {
  getSupplyReportBreakdowns,
  getSupplyReportSeries,
  getSupplyReportSummary,
} from "../lib/reports.server"

const reportFiltersSchema = z.object({
  dateFrom: z.number(),
  dateTo: z.number(),
  categoryId: z.number().optional(),
  vendorId: z.number().optional(),
  itemId: z.number().optional(),
  granularity: z.enum(["day", "week", "month"]),
})

export type ReportExportPayload = {
  filename: string
  contentType: string
  base64: string
}

function toPayload(
  filename: string,
  contentType: string,
  buffer: ArrayBuffer,
): ReportExportPayload {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return {
    filename,
    contentType,
    base64: btoa(binary),
  }
}

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replaceAll(":", "-")
}

function csvToBuffer(csv: string): ArrayBuffer {
  const encoded = new TextEncoder().encode(csv)
  return encoded.buffer.slice(
    encoded.byteOffset,
    encoded.byteOffset + encoded.byteLength,
  ) as ArrayBuffer
}

export const getSupplyReportSummaryFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => reportFiltersSchema.parse(data))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    return getSupplyReportSummary(data)
  })

export const getSupplyReportSeriesFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => reportFiltersSchema.parse(data))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    return getSupplyReportSeries(data)
  })

export const getSupplyReportBreakdownsFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => reportFiltersSchema.parse(data))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    return getSupplyReportBreakdowns(data)
  })

export const exportSupplyReportSeriesCsvFn = createServerFn({ method: "GET" })
  .validator((data: unknown) => reportFiltersSchema.parse(data))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const { buckets } = await getSupplyReportSeries(data)
    const csv = buildReportSeriesCsv(buckets)
    return toPayload(
      `supply-report-series-${stamp()}.csv`,
      "text/csv;charset=utf-8",
      csvToBuffer(csv),
    )
  })

export const exportSupplyReportBreakdownCsvFn = createServerFn({
  method: "GET",
})
  .validator((data: unknown) => reportFiltersSchema.parse(data))
  .handler(async ({ data }) => {
    await requireInventoryUserId()
    const breakdowns = await getSupplyReportBreakdowns(data)
    const csv = buildReportBreakdownCsv(breakdowns)
    return toPayload(
      `supply-report-breakdown-${stamp()}.csv`,
      "text/csv;charset=utf-8",
      csvToBuffer(csv),
    )
  })
